import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { CardButton } from '@/src/elements/button/card/CardButton'
import { useCommunityJoinSteps } from '@/src/features/community/ui/hooks/useCommunityJoinSteps'
import { useFraternityOnboardingMutation } from '@/src/features/fraternity/queries/useFraternityOnboardingMutation'
import { FraternityOnboardingWrapper } from '@/src/features/fraternity/ui/components/FraternityOnboardingWrapper'
import { ICON } from '@/src/general/enums/icons'
import { useTranslate } from '@/src/lang/useTranslate'

type TFraternityJoinScreenProps = TIdSlugParam & TSearchParamsParam

export const FraternityJoinScreen = ({ idSlug, searchParams }: TFraternityJoinScreenProps): ReactNode => {
  const { t } = useTranslate()
  const joinSteps = useCommunityJoinSteps({ idSlug, searchParams, stepName: 'fraternity' })
  const { goToNextStep, goToClose } = joinSteps
  const { mutateAsync, isPending: isSaving } = useFraternityOnboardingMutation()

  const handleJoinFraternity = async () => {
    await mutateAsync({ has_asked_fraternity: true })
    goToNextStep()
  }

  const handleAlreadyHave = async () => {
    await mutateAsync({ has_asked_fraternity: false })
    goToClose()
  }

  return (
    <FraternityOnboardingWrapper
      joinSteps={joinSteps}
      title={<FormattedMessage id="fraternity.join.title" />}
      subtitle={<FormattedMessage id="fraternity.join.subtitle" />}
    >
      <View className="gap-3">
        <CardButton
          startIcon={ICON.SEARCH}
          endIcon={ICON.CHEVRON_RIGHT}
          label={t('fraternity.join.find')}
          description={t('fraternity.join.find-description')}
          onPress={handleJoinFraternity}
          disabled={isSaving}
        />
        <CardButton
          startIcon={ICON.USERS}
          endIcon={ICON.CHEVRON_RIGHT}
          label={t('fraternity.join.already-have')}
          description={t('fraternity.join.already-have-description')}
          onPress={handleAlreadyHave}
          disabled={isSaving}
        />
      </View>
    </FraternityOnboardingWrapper>
  )
}
